import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, Send, ArrowLeft, Calculator } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { Currency, CurrencyInput } from "@/components/i18n";
import { useCountry } from "@/contexts/CountryContext";
import DashboardContainer from "../layout/DashboardContainer";
import InvoiceAPIService from "@/services/invoice-api";
import {
  CreateInvoiceRequest,
  CreateInvoiceLineItemRequest,
  AvailableContract,
} from "@/types/invoice";

interface LineItemFormData {
  product: string;
  description: string;
  rate: string;
  quantity: string;
  tax: string;
}

interface InvoiceFormData {
  contractorContractId: string;
  invoiceNumber: string;
  dueDate: string;
  notes: string;
  lineItems: LineItemFormData[];
}

const LineItemRow: React.FC<{
  item: LineItemFormData;
  index: number;
  onUpdate: (
    index: number,
    field: keyof LineItemFormData,
    value: string,
  ) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}> = ({ item, index, onUpdate, onRemove, canRemove }) => {
  const { formatCurrency, getCurrencySymbol } = useCountry();

  const calculateTotal = () => {
    const rate = parseFloat(item.rate) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    const tax = parseFloat(item.tax) || 0;
    return rate * quantity + tax;
  };

  const formatCurrencyLocal = (amount: number) => {
    // Use the context's formatCurrency instead
    return formatCurrency(amount);
  };

  return (
    <Card className="p-4">
      <div className="flex justify-between items-start mb-4">
        <h4 className="font-medium">Line Item {index + 1}</h4>
        {canRemove && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`product-${index}`}>Product/Service *</Label>
          <Input
            id={`product-${index}`}
            value={item.product}
            onChange={(e) => onUpdate(index, "product", e.target.value)}
            placeholder="e.g., Childcare Services"
            required
            data-testid="product-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`description-${index}`}>Description</Label>
          <Input
            id={`description-${index}`}
            value={item.description}
            onChange={(e) => onUpdate(index, "description", e.target.value)}
            placeholder="Optional description"
            data-testid="description-input"
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor={`rate-${index}`}>Rate ({getCurrencySymbol()}) *</Label>
          <Input
            id={`rate-${index}`}
            type="number"
            step="0.01"
            min="0"
            value={item.rate}
            onChange={(e) => onUpdate(index, "rate", e.target.value)}
            placeholder="0.00"
            required
            data-testid="rate-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`quantity-${index}`}>Quantity / Hours *</Label>
          <Input
            id={`quantity-${index}`}
            type="number"
            step="0.01"
            min="0.01"
            value={item.quantity}
            onChange={(e) => onUpdate(index, "quantity", e.target.value)}
            placeholder="1"
            required
            data-testid="quantity-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`tax-${index}`}>Tax ({getCurrencySymbol()})</Label>
          <Input
            id={`tax-${index}`}
            type="number"
            step="0.01"
            min="0"
            value={item.tax}
            onChange={(e) => onUpdate(index, "tax", e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div className="space-y-2">
          <Label>Total</Label>
          <div className="flex items-center h-10 px-3 py-2 border border-input bg-muted rounded-md">
            <Currency amount={calculateTotal()} />
          </div>
        </div>
      </div>
    </Card>
  );
};

export function CreateInvoiceForm({
  onCancel,
  onSuccess,
}: {
  onCancel: () => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const { currentCountry } = useCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [availableContracts, setAvailableContracts] = useState<
    AvailableContract[]
  >([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [formData, setFormData] = useState<InvoiceFormData>({
    contractorContractId: "",
    invoiceNumber: "",
    dueDate: "",
    notes: "",
    lineItems: [
      {
        product: "",
        description: "",
        rate: "",
        quantity: "0",
        tax: "0",
      },
    ],
  });

  // Fetch available contracts on component mount
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const response = await InvoiceAPIService.getAvailableContracts();
        setAvailableContracts(response.contracts);

        // Auto-select the first contract if available
        if (response.contracts.length > 0) {
          const firstContract = response.contracts[0];
          setFormData((prev) => ({
            ...prev,
            contractorContractId: firstContract.id,
            lineItems: [
              {
                product: firstContract.schedule_a_services_description || "",
                description: "",
                rate: firstContract.fee_rate?.toString() || "",
                quantity: "0",
                tax: "0",
              },
            ],
          }));
        }
      } catch (error) {
        console.error("Error fetching contracts:", error);
        toast({
          title: "Error",
          description: "Failed to load available contracts. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingContracts(false);
      }
    };

    fetchContracts();
  }, [toast]);

  const handleContractChange = (contractId: string) => {
    const selectedContract = availableContracts.find(
      (contract) => contract.id === contractId,
    );

    setFormData((prev) => ({
      ...prev,
      contractorContractId: contractId,
      lineItems: prev.lineItems.map((item, index) =>
        index === 0
          ? {
              ...item,
              product: selectedContract?.schedule_a_services_description || "",
              rate: selectedContract?.fee_rate?.toString() || "",
            }
          : item,
      ),
    }));
  };

  const updateLineItem = (
    index: number,
    field: keyof LineItemFormData,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addLineItem = () => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          product: "",
          description: "",
          rate: "",
          quantity: "0",
          tax: "0",
        },
      ],
    }));
  };

  const removeLineItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalTax = 0;

    formData.lineItems.forEach((item) => {
      const rate = parseFloat(item.rate) || 0;
      const quantity = parseFloat(item.quantity) || 0;
      const tax = parseFloat(item.tax) || 0;

      subtotal += rate * quantity;
      totalTax += tax;
    });

    return {
      subtotal,
      totalTax,
      grandTotal: subtotal + totalTax,
    };
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.contractorContractId) {
      errors.push("Please select a contract");
    }

    if (!formData.dueDate) {
      errors.push("Due date is required");
    } else {
      const dueDate = new Date(formData.dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (dueDate <= today) {
        errors.push("Due date must be in the future");
      }
    }

    if (formData.lineItems.length === 0) {
      errors.push("At least one line item is required");
    }

    formData.lineItems.forEach((item, index) => {
      if (!item.product.trim()) {
        errors.push(`Line item ${index + 1}: Product/Service is required`);
      }

      const rate = parseFloat(item.rate);
      if (isNaN(rate) || rate <= 0) {
        errors.push(`Line item ${index + 1}: Rate must be greater than 0`);
      }

      const quantity = parseFloat(item.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        errors.push(
          `Line item ${index + 1}: Quantity / Hours must be greater than 0`,
        );
      }

      const tax = parseFloat(item.tax);
      if (isNaN(tax) || tax < 0) {
        errors.push(`Line item ${index + 1}: Tax must be 0 or greater`);
      }
    });

    const { grandTotal } = calculateTotals();
    if (grandTotal <= 0) {
      errors.push("Invoice total must be greater than £0.00");
    }

    return errors;
  };

  const handleSubmit = async (
    isDraft: boolean = false,
    shouldSubmit: boolean = false,
  ) => {
    const errors = validateForm();
    if (errors.length > 0) {
      toast({
        title: "Validation Error",
        description: errors[0],
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const lineItems: CreateInvoiceLineItemRequest[] = formData.lineItems.map(
        (item) => ({
          product: item.product.trim(),
          description: item.description.trim() || undefined,
          rate: parseFloat(item.rate),
          quantity: parseFloat(item.quantity),
          tax: parseFloat(item.tax),
        }),
      );

      const requestData: CreateInvoiceRequest = {
        contractorContractId: formData.contractorContractId,
        dueDate: formData.dueDate,
        invoiceNumber: formData.invoiceNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        lineItems,
      };

      const response = await InvoiceAPIService.createInvoice(requestData);

      // If shouldSubmit is true, submit the invoice after creation
      if (shouldSubmit && response.invoice) {
        await InvoiceAPIService.submitInvoice(response.invoice.id);
        toast({
          title: "Success",
          description: "Invoice created and submitted successfully.",
        });
      } else {
        toast({
          title: "Success",
          description: `Invoice ${isDraft ? "saved as draft" : "created"} successfully.`,
        });
      }

      onSuccess();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast({
        title: "Error",
        description: "Failed to create invoice. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrencyDisplay = (amount: number) => {
    return currentCountry?.currencyCode
      ? new Intl.NumberFormat(
          currentCountry.code === 'US' ? 'en-US' : 'en-GB',
          {
            style: "currency",
            currency: currentCountry.currencyCode,
          }
        ).format(amount)
      : `£${amount.toFixed(2)}`;
  };

  const { subtotal, totalTax, grandTotal } = calculateTotals();

  const headerContent = (
    <div className="flex flex-col sm:flex-row gap-2">
      <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Invoices
      </Button>
    </div>
  );

  if (isLoadingContracts) {
    return (
      <DashboardContainer title="Create Invoice" headerContent={headerContent}>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">
              Loading available contracts...
            </p>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  if (availableContracts.length === 0) {
    return (
      <DashboardContainer title="Create Invoice" headerContent={headerContent}>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <p className="text-lg font-medium mb-2">
              No Active Contracts Found
            </p>
            <p className="text-muted-foreground mb-4">
              You need to have an active contract before creating invoices.
            </p>
            <Button onClick={onCancel} data-testid="back-to-invoices-button">Back to Invoices</Button>
          </div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer title="Create Invoice" headerContent={headerContent}>
      <div className="space-y-6">
        {/* Contract Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Selection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contract">Select Contract *</Label>
              <Select
                value={formData.contractorContractId}
                onValueChange={handleContractChange}
              >
                <SelectTrigger data-testid="contract-select">
                  <SelectValue placeholder="Choose a contract for this invoice" />
                </SelectTrigger>
                <SelectContent>
                  {availableContracts.map((contract) => (
                    <SelectItem key={contract.id} value={contract.id}>
                      {contract.contractTitle}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Only active contracts are available for invoicing
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Invoice Details */}
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      invoiceNumber: e.target.value,
                    }))
                  }
                  placeholder="e.g., INV-2024-001 (optional)"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to auto-generate
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      dueDate: e.target.value,
                    }))
                  }
                  min={
                    new Date(Date.now() + 86400000).toISOString().split("T")[0]
                  } // Tomorrow
                  required
                  data-testid="due-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Optional notes about this invoice..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Line Items
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addLineItem}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.lineItems.map((item, index) => (
              <LineItemRow
                key={index}
                item={item}
                index={index}
                onUpdate={updateLineItem}
                onRemove={removeLineItem}
                canRemove={formData.lineItems.length > 1}
              />
            ))}
          </CardContent>
        </Card>

        {/* Invoice Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calculator className="h-5 w-5 mr-2" />
              Invoice Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span><Currency amount={subtotal} /></span>
              </div>
              <div className="flex justify-between">
                <span>Total Tax:</span>
                <span><Currency amount={totalTax} /></span>
              </div>
              <div className="border-t pt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Grand Total:</span>
                  <span><Currency amount={grandTotal} /></span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={() => handleSubmit(true)}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <Save className="h-4 w-4 mr-2" />
            Save as Draft
          </Button>
          <Button
            onClick={() => handleSubmit(false)}
            disabled={isLoading}
            className="w-full sm:w-auto"
            data-testid="create-invoice-submit-button"
          >
            <Send className="h-4 w-4 mr-2" />
            {isLoading ? "Creating..." : "Create Invoice"}
          </Button>
          <Button
            onClick={() => handleSubmit(false, true)}
            disabled={isLoading}
            className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
          >
            <Send className="h-4 w-4 mr-2" />
            {isLoading ? "Creating & Submitting..." : "Create Invoice & Submit"}
          </Button>
        </div>
      </div>
    </DashboardContainer>
  );
}
